import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import Loading from './Loading';
import { apiErrorText, apiRequest, shortDate } from '../../utils';
import './CommsStats.css';

/* Does what we send on their behalf actually reach their people?

   ★ Three numbers, and deliberately no fourth. There is no OPEN rate anywhere on the
   platform — SendGrid opens are ignored by design — and no click rate, because clicks need a
   campaign id that company-event sends do not carry. A box reading 0% would be read as
   "nobody opened it" rather than "we do not measure that", so neither is drawn.

   ★ The bell is the exact half: a notification row carries the event id, so "sent" and "seen"
   are counts, not estimates. But `seen` means the person opened their notifications, not that
   they read this one — the feed flips every row at once and keeps no timestamp — which is why
   the word on screen is "seen the bell" and never "read".

   ★ The mail half can only be counted from the day a sent mail started recording which event
   it belonged to. The screen says when that was rather than showing a total that quietly
   begins mid-history. */

const PERIODS = ['month', 'quarter', 'year'];

const Tile = ({ n, label, tone }) => (
  <div className={`cst-tile ${tone || ''}`}>
    <span className="cst-tile__n">{n}</span>
    <span className="cst-tile__label">{label}</span>
  </div>
);

const CommsStats = () => {
  const { t, i18n } = useTranslation();
  const [period, setPeriod] = useState('month');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setData(null);
    setError(null);
    apiRequest('GET', `/company/comms/stats?period=${period}`)
      .then(setData)
      .catch((err) => setError(apiErrorText(t, err)));
  }, [period, t]);

  useEffect(() => { load(); }, [load]);

  const periods = (
    <div className="ce-tabs">
      {PERIODS.map((p) => (
        <button key={p} type="button" className={`ce-tab ${period === p ? 'is-on' : ''}`}
                onClick={() => setPeriod(p)}>
          {t(`period_${p}`)}
        </button>
      ))}
    </div>
  );

  if (error) return <>{periods}<p className="bravos-cal__notice">{error}</p></>;
  /* A named, visible wait rather than the bare `auth__spinner`, which is a white ring on a
     white card — invisible exactly here, where the counting takes seconds and a screen that
     shows nothing at all reads as a screen that broke. */
  if (data === null) return <>{periods}<Loading /></>;

  const { reach, bell, mail } = data;

  return (
    <div className="cst">
      {periods}

      {/* Who we could reach at all — the number that explains a disappointing send BEFORE
          it happens, rather than after. Live, not historical. */}
      <p className="bravos-cal__label mt-3">{t('statsReach')}</p>
      <div className="cst-tiles">
        <Tile n={reach.people} label={t('statsOnRoster')} />
        <Tile n={reach.mailable} label={t('statsEmailable')} tone="is-good" />
        {reach.no_email > 0 && <Tile n={reach.no_email} label={t('statsNoAddress')} tone="is-warn" />}
        {reach.suppressed > 0 && (
          <Tile n={reach.suppressed} label={t('statsOptedOut')} tone="is-warn" />
        )}
      </div>

      <p className="bravos-cal__label mt-3">{t('statsBell')}</p>
      <div className="cst-tiles">
        <Tile n={bell.sent} label={t('statsBellSent')} />
        <Tile n={bell.read} label={t('statsBellSeen')} tone="is-good" />
        <Tile n={`${bell.read_rate}%`} label={t('statsBellSeenRate')} />
      </div>
      <p className="ce-note">{t('statsBellNote')}</p>
      {bell.by_type.length > 0 && (
        <div className="cst-breakdown">
          {bell.by_type.map((row) => (
            <div key={row.message_name} className="cst-line">
              <span className="cst-line__label">
                {t(`msg_${row.message_name}_title`, { defaultValue: row.message_name })}
              </span>
              <span className="cst-line__n">{row.sent}</span>
              <span className="cst-line__sub">{t('statsSeenOf', { n: row.read })}</span>
            </div>
          ))}
        </div>
      )}

      <p className="bravos-cal__label mt-3">{t('statsMail')}</p>
      {mail.since ? (
        <>
          <div className="cst-tiles">
            <Tile n={mail.sent} label={t('statsMailSent')} />
            <Tile n={mail.people} label={t('statsMailPeople')} />
          </div>
          {/* The window is stated, not implied. Nothing sent before the platform started
              recording which event a mail belonged to can ever be counted here. */}
          <p className="ce-note">
            {t('statsMailSince', { date: shortDate(mail.since, i18n.language) })}
          </p>
          <div className="cst-breakdown">
            {mail.by_type.map((row) => (
              <div key={row.message_name} className="cst-line">
                <span className="cst-line__label">
                  {t(`msg_${row.message_name}_title`, { defaultValue: row.message_name })}
                </span>
                <span className="cst-line__n">{row.sent}</span>
              </div>
            ))}
          </div>
        </>
      ) : (
        <p className="ce-note">{t('statsMailNothingYet')}</p>
      )}

      {/* Said plainly rather than left as an absence somebody has to notice. */}
      <p className="ce-note cst-limit">
        <Icon name="info" /> {t('statsNotMeasured')}
      </p>
    </div>
  );
};

export default CommsStats;
